import { TaskListItem } from '@myapp/contract'
import { getTopPx, getHeightPx } from '../model/calendarConstants'

const TASK_WORK_COLOR = '#D96868'
const TASK_TRAVEL_COLOR = '#E89898'
const TASK_COMPLETED_WORK_COLOR = '#3BC67D'
const TASK_COMPLETED_TRAVEL_COLOR = '#8B97D9'

interface Props {
  task: TaskListItem
  onClick: () => void
  left?: string
  zIndex?: number
}

export const WorkerTaskBlock = ({ task, onClick, left, zIndex }: Props) => {
  if (!task.plannedStart) return null

  const isCompleted = task.status === 'COMPLETED'
  const isInProgress = !!task.actualStart && !task.actualEnd
  const useActuals = isCompleted || isInProgress
  const workColor = isCompleted ? TASK_COMPLETED_WORK_COLOR : TASK_WORK_COLOR
  const travelColor = isCompleted ? TASK_COMPLETED_TRAVEL_COLOR : TASK_TRAVEL_COLOR
  const effectiveTravelColor = isInProgress ? TASK_COMPLETED_TRAVEL_COLOR : travelColor

  const travelDuration = useActuals ? (task.actualTravelDuration ?? task.plannedTravelDuration) : task.plannedTravelDuration
  const workDuration = useActuals ? (task.actualWorkDuration ?? task.plannedWorkDuration) : task.plannedWorkDuration
  const departureDuration = useActuals ? (task.actualDepartureDuration ?? task.plannedDepartureDuration) : task.plannedDepartureDuration

  // actualStart = реальний час початку роботи (без від'їзду та дороги)
  const workStart = useActuals && task.actualStart
    ? new Date(task.actualStart)
    : new Date(task.plannedStart)

  const travelStart = new Date(workStart.getTime() - travelDuration * 60_000)

  const totalMinutes = travelDuration + workDuration + departureDuration

  const top = getTopPx(travelStart.toISOString())
  const height = Math.max(getHeightPx(totalMinutes), 20)

  const travelPx = getHeightPx(travelDuration)
  const workPx = getHeightPx(workDuration)
  const departurePx = getHeightPx(departureDuration)

  const timeLabel = `${String(workStart.getHours()).padStart(2, '0')}:${String(workStart.getMinutes()).padStart(2, '0')}`

  return (
    <div
      data-block
      className="absolute right-0.5 flex flex-col rounded overflow-hidden cursor-pointer gap-px"
      style={{ top, height, left: left ?? '2px', zIndex: zIndex ?? 10 }}
      onClick={(e) => { e.stopPropagation(); onClick() }}
    >
      {travelDuration > 0 && (
        <div
          className="flex items-center px-1 text-white text-[12px] font-semibold overflow-hidden shrink-0 rounded-sm"
          style={{ backgroundColor: effectiveTravelColor, height: travelPx, minHeight: 12 }}
        >
          Anfahrt
        </div>
      )}
      <div
        className="relative flex flex-col justify-start px-1 text-white text-[12px] font-semibold overflow-hidden rounded-sm py-1"
        style={{ backgroundColor: workColor, height: workPx, minHeight: 16, flexShrink: 0 }}
      >
        {isInProgress && (
          <>
            <div
              className="absolute inset-0 rounded-sm ring-2 ring-inset ring-white animate-pulse pointer-events-none"
              aria-hidden
            />
            <span className="self-start inline-flex items-center gap-1 px-1 py-px rounded bg-red-500 text-white text-[9px] font-bold uppercase leading-none max-w-full mb-0.5">
              <span className="w-1 h-1 rounded-full bg-white animate-pulse shrink-0" />
              <span className="truncate">Läuft</span>
            </span>
          </>
        )}
        <span className="font-semibold leading-tight">{timeLabel}</span>
        <span className="leading-tight line-clamp-2 break-words">{task.title}</span>
      </div>
      {departureDuration > 0 && (
        <div
          className="flex items-center px-1 text-white text-[12px] font-semibold overflow-hidden shrink-0 rounded-sm"
          style={{ backgroundColor: travelColor, height: departurePx, minHeight: 12 }}
        >
          Abfahrt
        </div>
      )}
    </div>
  )
}
